import { useEffect, useMemo, useState } from "react";

import type { ProjectDisplayData } from "../app/projectData";
import { TechnicalDetails } from "../components/TechnicalDetails";
import type { ApartmentSolutionDisplayViewModel } from "../viewmodel/solutionViewAdapter";
import { cx } from "./shared";
import { GlossaryAcronymText, Term } from "./Glossary";

type RailNoteKind =
  | "clarification"
  | "exclusion"
  | "commissioning"
  | "requirement-amendment";

interface NoteAttachmentOption {
  id: string;
  label: string;
  kind: "project" | "requirement" | "item" | "risk" | "decision";
}

const noteAttachmentGroupLabels: Record<NoteAttachmentOption["kind"], string> = {
  project: "Project",
  decision: "Decisions",
  risk: "Risks",
  requirement: "Requirements",
  item: "Specification items"
};

const noteAttachmentPrefixes: Record<NoteAttachmentOption["kind"], string> = {
  project: "Project · ",
  decision: "Decision · ",
  risk: "Risk · ",
  requirement: "Req · ",
  item: "Item · "
};

const noteAttachmentGroupOrder: NoteAttachmentOption["kind"][] = [
  "project",
  "decision",
  "risk",
  "requirement",
  "item"
];

interface LocalRailNote {
  id: string;
  kind: RailNoteKind;
  text: string;
  author: string;
  timestamp: string;
  tags: string[];
  attachmentId: string;
  attachmentLabel: string;
  noPriceImpact: boolean;
}

const railNoteTone: Record<RailNoteKind, { label: string; className: string }> = {
  clarification: {
    label: "Clarification",
    className: "bg-[#EAF1E6] text-[#4E7644]"
  },
  exclusion: {
    label: "Exclusion",
    className: "bg-[#EFE6DA] text-[#8A6A3E]"
  },
  commissioning: {
    label: "Commissioning",
    className: "bg-[#ECEAF6] text-[#6A5E97]"
  },
  "requirement-amendment": {
    label: "Requirement amendment",
    className: "bg-[#E2ECF4] text-[#3E6E9A]"
  }
};

const splitTags = (value: string): string[] =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

const keywordTags = (text: string): string[] => {
  const lowered = text.toLowerCase();
  const candidates = [
    "FCU",
    "UFH",
    "Netmaster",
    "Modbus",
    "meter",
    "leak",
    "Edge",
    "BMS",
    "RDU",
    "condensate",
    "PSU",
    "I/O"
  ];

  return candidates.filter((tag) => lowered.includes(tag.toLowerCase()));
};

export const createLocalRailNote = ({
  text,
  kind,
  tagInput,
  attachment
}: {
  text: string;
  kind: RailNoteKind;
  tagInput: string;
  attachment: NoteAttachmentOption;
}): LocalRailNote => ({
  id: `note-${Date.now()}`,
  kind,
  text: text.trim(),
  author: "J. Hartley",
  timestamp: "Just now",
  tags: [...new Set([...splitTags(tagInput), ...keywordTags(text)])],
  attachmentId: attachment.id,
  attachmentLabel: attachment.label,
  noPriceImpact: kind === "commissioning"
});

const fallbackAttachment: NoteAttachmentOption = {
  id: "project",
  label: "Project",
  kind: "project"
};

const projectAttachmentFrom = (
  options: readonly NoteAttachmentOption[]
): NoteAttachmentOption =>
  options.find((option) => option.kind === "project") ??
  options[0] ??
  fallbackAttachment;

const seededRailNotes = (projectAttachment: NoteAttachmentOption): LocalRailNote[] => {
  if (projectAttachment.id !== "carrington") {
    return [];
  }

  return [
    {
      id: "note-design-tight-fcu",
      kind: "clarification",
      text: "Living-room FCU on Type A is tight on inputs; keep an eye on the condensate pump point when we firm up the quote.",
      author: "J. Hartley",
      timestamp: "Today · 09:14",
      tags: ["FCU", "Type A", "condensate"],
      attachmentId: projectAttachment.id,
      attachmentLabel: projectAttachment.label,
      noPriceImpact: false
    },
    {
      id: "note-design-bms-commissioning",
      kind: "commissioning",
      text: "Client verbally confirmed every Netmaster commissions onto the landlord BMS. Get it in writing.",
      author: "J. Hartley",
      timestamp: "Yesterday · 16:40",
      tags: ["Netmaster", "BMS"],
      attachmentId: projectAttachment.id,
      attachmentLabel: projectAttachment.label,
      noPriceImpact: true
    }
  ];
};

export const seededRailNoteCopyTextsForProject = (projectId: string): string[] =>
  seededRailNotes({
    id: projectId,
    label: "Project",
    kind: "project"
  }).map((note) => note.text);

const compactNotesRailQuery = "(max-width: 1439px)";

type NotesRailViewportHost = typeof globalThis & {
  matchMedia?: (query: string) => MediaQueryList;
};

const notesRailMediaQuery = (): MediaQueryList | null => {
  const matchMedia = (globalThis as NotesRailViewportHost).matchMedia;

  return typeof matchMedia === "function" ? matchMedia(compactNotesRailQuery) : null;
};

const isCompactNotesRailViewport = (): boolean =>
  notesRailMediaQuery()?.matches ?? false;

// A handful of Edge/Infrastructure workspace items fall back to a long
// "short name - full description" placeholder (no real catalog SKU yet, see
// `infrastructureLabel` in solutionViewAdapter.ts, e.g. "Edge gateway -
// apartment gateway for BACnet-to-cloud leak detection"). Native <option>
// elements can't host the Term/tooltip glossary treatment used elsewhere on
// this screen, so keep just the short name here rather than risk a bare
// glossary acronym landing in an unwrappable, plain-text-only element.
const shortItemLabel = (name: string): string => name.split(/\s+[-–—]\s+/)[0] ?? name;

export const noteAttachmentOptionsFor = ({
  projectData,
  workspaceView
}: {
  projectData: ProjectDisplayData | null;
  workspaceView: ApartmentSolutionDisplayViewModel | null;
}): NoteAttachmentOption[] => {
  const projectOption: NoteAttachmentOption = projectData
    ? {
        id: projectData.projectFixture.project.projectId,
        label: `Project · ${projectData.cockpitView.title}`,
        kind: "project"
      }
    : fallbackAttachment;
  const requirementOptions =
    projectData?.requirementStudioView.requirements.slice(0, 36).map((requirement) => ({
      id: requirement.id,
      label: `Req · ${requirement.kindLabel}`,
      kind: "requirement" as const
    })) ?? [];
  const riskOptions =
    projectData?.riskLedgerView.risks.map((risk) => ({
      id: risk.id,
      label: `Risk · ${risk.apartmentTypeLabel} · ${risk.title}`,
      kind: "risk" as const
    })) ?? [];
  const decisionOptions =
    projectData?.decisionsHubView.decisions.map((decision) => ({
      id: decision.id,
      label: `Decision · ${decision.themeLabel} · ${decision.title}`,
      kind: "decision" as const
    })) ?? [];
  const itemOptions =
    workspaceView?.items.slice(0, 24).map((item) => ({
      id: item.id,
      label: `Item · ${shortItemLabel(item.name)}`,
      kind: "item" as const
    })) ?? [];

  return dedupeNoteAttachmentOptions([
    projectOption,
    ...decisionOptions,
    ...riskOptions,
    ...requirementOptions,
    ...itemOptions
  ]);
};

function dedupeNoteAttachmentOptions(
  options: readonly NoteAttachmentOption[]
): NoteAttachmentOption[] {
  const seen = new Set<string>();

  return options.filter((option) => {
    const key = `${option.kind}:${option.label.replace(/\s+/gu, " ").trim().toLocaleLowerCase()}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function noteAttachmentGroups(
  options: readonly NoteAttachmentOption[]
): Array<{
  kind: NoteAttachmentOption["kind"];
  label: string;
  options: NoteAttachmentOption[];
}> {
  return noteAttachmentGroupOrder.flatMap((kind) => {
    const groupOptions = options.filter((option) => option.kind === kind);

    return groupOptions.length > 0
      ? [{ kind, label: noteAttachmentGroupLabels[kind], options: groupOptions }]
      : [];
  });
}

function noteAttachmentSelectLabel(option: NoteAttachmentOption): string {
  const prefix = noteAttachmentPrefixes[option.kind];

  return option.label.startsWith(prefix) ? option.label.slice(prefix.length) : option.label;
}

function CopilotRouteCta({ onOpenCopilot }: { onOpenCopilot: () => void }) {
  return (
    <section className="rounded-xl border border-[#D7E1EA] bg-[#F6FAFD] p-3">
      <div className="text-[10px] font-bold uppercase tracking-[.1em] text-[#3E6E9A]">
        Live co-pilot
      </div>
      <div className="mt-0.5 text-[13px] font-extrabold text-[var(--ink)]">
        One co-pilot workspace
      </div>
      <p className="m-0 mt-2 text-[11.5px] leading-[1.45] text-[var(--muted)]">
        Ask, preview and confirm live changes in the dedicated co-pilot surface. Notes
        stay here for quick capture.
      </p>
      <button
        type="button"
        onClick={onOpenCopilot}
        className="mt-3 w-full rounded-[10px] bg-[var(--ink)] px-3 py-2.5 text-[12px] font-bold text-[var(--onSolid)]"
      >
        Open live co-pilot
      </button>
    </section>
  );
}

export function NotesRail({
  attachmentOptions,
  onOpenCopilot
}: {
  attachmentOptions: readonly NoteAttachmentOption[];
  onOpenCopilot: () => void;
}) {
  const initialProjectAttachment = projectAttachmentFrom(attachmentOptions);
  const [compact, setCompact] = useState(() => isCompactNotesRailViewport());
  const [open, setOpen] = useState(() => !isCompactNotesRailViewport());
  const [notes, setNotes] = useState<LocalRailNote[]>(() =>
    seededRailNotes(initialProjectAttachment)
  );
  const [draft, setDraft] = useState("");
  const [draftKind, setDraftKind] = useState<RailNoteKind>("clarification");
  const [tagInput, setTagInput] = useState("");
  const [manualCopyNoteId, setManualCopyNoteId] = useState<string | null>(null);
  const [attachmentId, setAttachmentId] = useState(
    attachmentOptions[0]?.id ?? fallbackAttachment.id
  );
  const renderedAttachmentOptions = useMemo(
    () =>
      dedupeNoteAttachmentOptions(
        attachmentOptions.length > 0 ? attachmentOptions : [fallbackAttachment]
      ),
    [attachmentOptions]
  );
  const renderedAttachmentGroups = useMemo(
    () => noteAttachmentGroups(renderedAttachmentOptions),
    [renderedAttachmentOptions]
  );
  const projectAttachment = useMemo(
    () => projectAttachmentFrom(renderedAttachmentOptions),
    [renderedAttachmentOptions]
  );
  const selectedAttachment =
    renderedAttachmentOptions.find((option) => option.id === attachmentId) ??
    renderedAttachmentOptions[0] ??
    fallbackAttachment;
  const notesTechnicalRawData = {
    surface: "notes-rail",
    statusBadge: "AI-parsed",
    attachmentMode: "local-note-attachments",
    noteCount: notes.length,
    selectedAttachmentId: selectedAttachment.id,
    availableAttachmentIds: renderedAttachmentOptions.map((option) => option.id)
  };

  useEffect(() => {
    setNotes(seededRailNotes(projectAttachment));
    setAttachmentId(projectAttachment.id);
  }, [projectAttachment.id, projectAttachment.label]);

  useEffect(() => {
    if (!renderedAttachmentOptions.some((option) => option.id === attachmentId)) {
      setAttachmentId(renderedAttachmentOptions[0]?.id ?? fallbackAttachment.id);
    }
  }, [attachmentId, renderedAttachmentOptions]);

  useEffect(() => {
    const media = notesRailMediaQuery();

    if (!media) {
      return;
    }

    const syncRailMode = () => {
      setCompact(media.matches);
      setOpen(!media.matches);
    };

    syncRailMode();
    media.addEventListener("change", syncRailMode);

    return () => media.removeEventListener("change", syncRailMode);
  }, []);

  const saveNote = () => {
    if (draft.trim().length === 0) {
      return;
    }

    setNotes((current) => [
      createLocalRailNote({
        text: draft,
        kind: draftKind,
        tagInput,
        attachment: selectedAttachment
      }),
      ...current
    ]);
    setDraft("");
    setTagInput("");
  };
  const enhanceDraft = () => {
    const next = draft.trim().replace(/\s+/g, " ");

    if (!next) {
      return;
    }

    setDraft(
      `${next.charAt(0).toUpperCase()}${next.slice(1)}${/[.!?]$/.test(next) ? "" : "."}`
    );
  };
  const copyNote = async (note: LocalRailNote) => {
    const clipboard = globalThis.navigator?.clipboard;
    const writeText = clipboard?.writeText;

    if (!writeText) {
      setManualCopyNoteId(note.id);

      return;
    }

    try {
      await writeText.call(clipboard, note.text);
      setManualCopyNoteId(null);
    } catch {
      setManualCopyNoteId(note.id);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded="false"
        title="Show notes"
        className="relative order-first z-[45] mx-4 mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-[11px] border border-[var(--line)] bg-[var(--ink)] px-3.5 py-2.5 text-[12px] font-bold text-[var(--onSolid)] shadow-[0_10px_28px_-14px_rgba(0,0,0,.45)] lg:fixed lg:order-none lg:bottom-4 lg:right-4 lg:mx-0 lg:mt-0 2xl:bottom-[18px] 2xl:right-[18px] 2xl:px-[15px] 2xl:py-[9px] 2xl:text-[12.5px]"
      >
        Project notes
        <span className="text-[11px] font-bold opacity-70">{notes.length}</span>
      </button>
    );
  }

  return (
    <aside
      aria-label="Project notes"
      className={cx(
        "flex flex-col bg-[var(--rail)]",
        compact
          ? "fixed bottom-3 right-3 top-3 z-40 w-[min(360px,calc(100vw-24px))] rounded-[16px] border border-[var(--line)] shadow-[0_24px_70px_-30px_rgba(0,0,0,.55)]"
          : "sticky top-0 h-screen w-[360px] flex-none border-l border-[var(--line)]"
      )}
    >
      <div className="border-b border-[var(--line)] px-[18px] pb-[14px] pt-[18px]">
        <div className="flex items-center gap-[9px]">
          <span className="text-sm font-extrabold tracking-[-.01em]">
            Project notes
          </span>
          <span className="rounded-full bg-[var(--chip)] px-2 py-px text-[11px] font-bold text-[var(--muted)]">
            {notes.length}
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-expanded="true"
            title="Hide notes"
            className="ml-auto flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-sm text-[var(--muted2)] hover:bg-[var(--chip)]"
          >
            ›
          </button>
        </div>
        <p className="m-0 mt-2 text-[11.5px] font-semibold leading-[1.45] text-[var(--muted)]">
          Capture clarifications, assumptions and scope notes for this project.
        </p>
        <div className="mt-3 rounded-[11px] border border-[var(--line3)] bg-[var(--surface)] p-2.5">
          <textarea
            aria-label="Write a note"
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            placeholder="Write a note..."
            className="min-h-[54px] w-full resize-y border-none bg-transparent text-[12.5px] leading-[1.45] text-[var(--ink)] outline-none"
          />
          <div className="mt-2 grid gap-2">
            <select
              aria-label="Note type"
              value={draftKind}
              onChange={(event) =>
                setDraftKind(event.currentTarget.value as RailNoteKind)
              }
              className="min-w-0 rounded-lg border border-[var(--line3)] bg-[var(--bg)] px-2.5 py-1.5 text-[11.5px] font-bold text-[var(--ink2)]"
            >
              <option value="clarification">Clarification</option>
              <option value="exclusion">Exclusion</option>
              <option value="commissioning">Commissioning · no price impact</option>
              <option value="requirement-amendment">Requirement amendment</option>
            </select>
            <input
              aria-label="Tags, comma separated"
              value={tagInput}
              onChange={(event) => setTagInput(event.currentTarget.value)}
              placeholder="Tags, comma separated"
              className="min-w-0 rounded-lg border border-[var(--line3)] bg-[var(--bg)] px-2.5 py-1.5 text-[11.5px] font-semibold text-[var(--ink2)] outline-none focus:border-[var(--ink)]"
            />
            <select
              aria-label="Attach note to"
              value={selectedAttachment.id}
              onChange={(event) => setAttachmentId(event.currentTarget.value)}
              className="min-w-0 rounded-lg border border-[var(--line3)] bg-[var(--bg)] px-2.5 py-1.5 text-[11.5px] font-bold text-[var(--ink2)]"
            >
              {renderedAttachmentGroups.map((group) => (
                <optgroup key={group.kind} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {noteAttachmentSelectLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="mt-2 flex items-center gap-[7px]">
            <button
              type="button"
              onClick={enhanceDraft}
              className="rounded-lg bg-[#ECEAF6] px-2.5 py-1.5 text-[11px] font-bold text-[#6A5E97]"
            >
              Tidy
            </button>
            <button
              type="button"
              onClick={saveNote}
              className="ml-auto rounded-lg bg-[var(--ink)] px-[14px] py-[7px] text-xs font-bold text-[var(--onSolid)] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={draft.trim().length === 0}
            >
              Save note
            </button>
          </div>
        </div>
        <TechnicalDetails
          className="mt-3"
          sources={[
            {
              label: "Project notes",
              detail:
                "Notes are internal project guidance and are not rendered verbatim as client output."
            }
          ]}
          checks={[
            {
              label: "Processing status",
              detail:
                "AI-parsed note metadata and attachment routing stay in technical details."
            }
          ]}
          rawData={notesTechnicalRawData}
          data-testid="notes-rail-technical-details"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2.5 overflow-auto p-3">
        <CopilotRouteCta onOpenCopilot={onOpenCopilot} />
        {notes.map((note) => {
          const tone = railNoteTone[note.kind];

          return (
            <article
              key={note.id}
              className="rounded-xl border border-[var(--line)] bg-[var(--surface)] px-[13px] py-3"
            >
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={cx(
                    "rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[.05em]",
                    tone.className
                  )}
                >
                  {tone.label}
                </span>
                {note.noPriceImpact ? (
                  <span className="ml-auto text-[10px] font-semibold text-[var(--faint)]">
                    no price impact
                  </span>
                ) : null}
              </div>
              <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--ink2)]">
                {/* Display-only glossary wrapping over user note content — the
                    underlying stored `note.text` string is never mutated. */}
                <GlossaryAcronymText value={note.text} />
              </p>
              {note.tags.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {note.tags.map((tag) => (
                    <span
                      key={`${note.id}-${tag}`}
                      className="rounded-md bg-[var(--chip)] px-2 py-0.5 text-[10px] font-bold text-[var(--muted)]"
                    >
                      <Term name={tag}>{tag}</Term>
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="relative mt-2 min-w-0 text-[10.5px] font-semibold text-[#6A5E97]">
                <span
                  data-testid="note-current-attachment"
                  tabIndex={0}
                  title={note.attachmentLabel}
                  aria-label={`Current attachment: ${note.attachmentLabel}`}
                  className="peer block max-w-full truncate rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[#6A5E97] focus-visible:ring-offset-1"
                >
                  ↳ {note.attachmentLabel}
                </span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-0 z-10 mb-1 hidden max-w-[300px] rounded-[6px] border border-[var(--line3)] bg-[var(--ink)] px-2 py-1.5 text-[10.5px] font-bold text-[var(--onSolid)] shadow-lg peer-hover:block peer-focus:block"
                >
                  {note.attachmentLabel}
                </span>
              </div>
              {manualCopyNoteId === note.id ? (
                <div
                  role="status"
                  className="mt-2 rounded-[10px] border border-[#E9D3AD] bg-[#F6EEDD] px-3 py-2"
                >
                  <div className="text-[10.5px] font-extrabold uppercase tracking-[.08em] text-[#8A6A2E]">
                    Clipboard blocked
                  </div>
                  <p className="m-0 mt-1 text-[11.5px] font-semibold leading-[1.45] text-[var(--muted)]">
                    Select the note text below and copy it manually.
                  </p>
                  <textarea
                    readOnly
                    aria-label="Manual copy note text"
                    value={note.text}
                    onFocus={(event) => event.currentTarget.select()}
                    className="mt-2 h-[72px] w-full resize-none rounded-[8px] border border-[#E9D3AD] bg-[var(--surface)] px-2.5 py-2 text-[11.5px] font-semibold leading-[1.4] text-[var(--ink2)] outline-none focus:border-[#8A6A2E]"
                  />
                </div>
              ) : null}
              <div className="mt-2 flex items-center gap-1 border-t border-[var(--line2)] pt-2">
                <span className="flex-1 text-[10.5px] font-semibold text-[var(--faint)]">
                  {note.author} · {note.timestamp}
                </span>
                <button
                  type="button"
                  onClick={() => void copyNote(note)}
                  className="rounded-md px-2 py-1 text-[11px] font-bold text-[var(--muted)] hover:bg-[var(--chip)]"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setNotes((current) =>
                      current.filter((candidate) => candidate.id !== note.id)
                    )
                  }
                  className="rounded-md px-2 py-1 text-[11px] font-bold text-[#BC4A3C] hover:bg-[#F6E7E4]"
                >
                  Delete
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </aside>
  );
}
